import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Generic CRUD hook for the finance tables that share the same shape
// (id + user_id + plain columns). The medications/supplements pattern
// in useMedications etc. would be five near-identical files; this
// collapses them into one.
//
// Pass:
//   table — Supabase table name
//   filter — optional ({ column, op: 'eq'|'is'|'neq', value })[] applied
//            on every fetch (e.g. installments default to status='active')
//   orderBy — optional { column, ascending }; defaults to created_at desc
export function useFinanceTable({ table, filter = [], orderBy } = {}) {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    let query = supabase.from(table).select('*').eq('user_id', user.id)
    for (const f of filter) {
      if (f.op === 'is') query = query.is(f.column, f.value)
      else if (f.op === 'neq') query = query.neq(f.column, f.value)
      else query = query.eq(f.column, f.value)
    }
    const ord = orderBy || { column: 'created_at', ascending: false }
    query = query.order(ord.column, { ascending: ord.ascending })
    const { data, error: err } = await query
    if (err) {
      // Surface the error to the console too — the page UI only
      // renders a generic EmptyState when rows is empty, so a failed
      // query (bad column name, broken filter) is otherwise invisible.
      // The setError above feeds the page-level error state for any
      // caller that wants to display it.
      console.error(`[useFinanceTable] ${table} fetch failed:`, err)
      setError(err.message)
    } else {
      setRows(data || [])
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, table, filter, orderBy])

  useEffect(() => { fetch() }, [fetch])

  const create = async (values) => {
    if (!user) return null
    const { data, error: err } = await supabase
      .from(table)
      .insert({ ...values, user_id: user.id })
      .select('*')
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    await fetch()
    return data
  }

  const update = async (id, patch) => {
    const { error: err } = await supabase
      .from(table)
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  const remove = async (id) => {
    const { error: err } = await supabase.from(table).delete().eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  return { rows, loading, error, create, update, remove, refetch: fetch }
}
